import { AUTOCOMPLETE_VALUES } from '../constants/autocompleteValues'
import { countries } from '../assets/countries'

export const getAddressFields = (
  l10n,
  isCountyStateRequiredField,
  isCountyStateFieldAvailable,
  countyStateLabel,
  isAddressField2NotAvailable
) => [
  {
    name: 'addressLine1',
    title: l10n.t('Street Line 1'),
    autocomplete: AUTOCOMPLETE_VALUES.CARD_ADDRESS_LINE_1,
    required: true,
    available: true,
    errorMessage: l10n.t(
      'FIRST_LINE_ADDRESS_ERROR',
    ),
  },
  {
    name: 'addressLine2',
    title: `${l10n.t('Street Line 2')} (${l10n.t('optional')})`,
    autocomplete: AUTOCOMPLETE_VALUES.CARD_ADDRESS_LINE_2,
    required: false,
    available: !isAddressField2NotAvailable,
  },
  {
    name: 'city',
    title: l10n.t('City/Town'),
    autocomplete: AUTOCOMPLETE_VALUES.CARD_ADDRESS_CITY,
    required: true,
    available: true,
    errorMessage: l10n.t('Please enter the city / town of your address'),
  },
  {
    name: 'state',
    title: countyStateLabel,
    autocomplete: AUTOCOMPLETE_VALUES.CARD_COUNTY_STATE,
    required: isCountyStateRequiredField,
    available: isCountyStateFieldAvailable,
  },
  {
    name: 'postalCode',
    title: `${l10n.t('Postcode')}`,
    autocomplete: AUTOCOMPLETE_VALUES.CARD_ADDRESS_POSTAL_CODE,
    required: false,
    available: true,
  },
]

export const getAddressLine = defaultBillingAddress => {
  const { addressLine1, addressLine2, city, country, postalCode, state } =
    defaultBillingAddress
  return [addressLine1, addressLine2, city, state, country, postalCode]
    .filter(i => Boolean(i))
    .join(', ')
}

export const getAddressItemsByKey = (address, key) =>
  address.find(el => el[key] !== undefined)

export const getCountryCode = (countryName, l10n) =>
  countries.find(country => l10n.t(country.NAME) === countryName)?.CODE

export const getCountryName = (countryCode, l10n) => {
  const country = countries.find(country => country.CODE === countryCode)
  return country ? l10n.t(country?.NAME) : ''
}

/**
 * Merges a billing address override returned from a UI hook
 * (onInitialised / onMethodSelecting / onMethodSelected / onAuthorising)
 * with the current billing address.
 *
 * @param {object} currentBillingAddress - the billing address currently in state
 * @param {object} [overrideValues] - value resolved/returned from the hook
 * @returns {object|null} the merged billing address, or null when there is no override
 */
export const mergeBillingAddressOverride = (
  currentBillingAddress,
  overrideValues,
) => {
  if (!overrideValues?.billingAddress) {
    return null
  }

  return {
    ...currentBillingAddress,
    ...overrideValues.billingAddress,
  }
}

/**
 * Builds the effective billing address that should be used for authorization.
 * The accumulated hook override (from onInitialised / onMethodSelecting /
 * onMethodSelected) is authoritative and is applied on top of the base address
 * (e.g. the card form values), so tenant overrides win on a per-field basis.
 *
 * @param {object} baseBillingAddress - base address (e.g. card form values)
 * @param {object} [hookOverride] - accumulated hook billing-address override
 * @returns {object} the effective billing address
 */
export const applyHookBillingAddressOverride = (
  baseBillingAddress,
  hookOverride,
) => {
  if (!hookOverride || !Object.keys(hookOverride).length) {
    return { ...baseBillingAddress }
  }

  return {
    ...baseBillingAddress,
    ...hookOverride,
  }
}
